import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ShoppingBag, FileSpreadsheet, Loader2, Check, Search, PackageCheck } from "lucide-react";

const STATUS = {
  to_prepare: { label: "À préparer", cls: "bg-amber-50 text-amber-700" },
  ready: { label: "Prête", cls: "bg-sky-50 text-sky-700" },
  delivered: { label: "Remise", cls: "bg-emerald-50 text-emerald-700" },
  canceled: { label: "Annulée", cls: "bg-stone-100 text-stone-500" },
  pending: { label: "Paiement en attente", cls: "bg-stone-100 text-stone-500" },
};

const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

const ShopOrdersAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/shop/orders"); setOrders(data || []); }
    catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (status && o.status !== status) return false;
      if (!needle) return true;
      return [o.orderNumber, o.parentName, o.email, (o.items || []).map((i) => i.name).join(" ")].join(" ").toLowerCase().includes(needle);
    });
  }, [orders, status, q]);

  const revenue = useMemo(() => shown.filter((o) => o.paymentStatus === "paid").reduce((s, o) => s + Number(o.total || 0), 0), [shown]);

  const setOrderStatus = async (o, next) => {
    try {
      await api.patch(`/admin/shop/orders/${o.id}`, { status: next });
      toast.success(next === "delivered" ? "Commande marquée comme remise" : "Statut mis à jour");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const exportXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/shop/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `elan-danse-boutique-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch { toast.error("Erreur lors de l'export"); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="shop-orders-admin">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800 flex items-center gap-2"><ShoppingBag size={24} /> Commandes boutique</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Suivi des ventes de goodies : à préparer, prêtes, remises.</p>
        </div>
        <button onClick={exportXlsx} disabled={downloading} className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-full text-sm disabled:opacity-50 shrink-0" data-testid="shop-orders-export">
          {downloading ? <Loader2 className="animate-spin" size={15} /> : <FileSpreadsheet size={15} />} Export Excel
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une commande, une famille, un article…" data-testid="shop-orders-search" className="w-full bg-stone-50 border border-stone-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="shop-orders-status-filter" className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm">
            <option value="">Tous les statuts</option>
            <option value="to_prepare">À préparer</option>
            <option value="ready">Prête</option>
            <option value="delivered">Remise</option>
            <option value="pending">Paiement en attente</option>
            <option value="canceled">Annulée</option>
          </select>
        </div>

        <div className="flex gap-6 text-sm text-stone-600 mb-4">
          <span data-testid="shop-orders-count">{shown.length} commande(s)</span>
          <span>Chiffre d&apos;affaires : <strong className="text-deep-rose" data-testid="shop-orders-revenue">{fmtEur(revenue)}</strong></span>
        </div>

        {loading ? (
          <div className="py-10 text-center text-stone-400"><Loader2 className="animate-spin inline" size={20} /></div>
        ) : shown.length === 0 ? (
          <p className="text-stone-400 italic text-sm py-6 text-center">Aucune commande.</p>
        ) : (
          <div className="space-y-2.5">
            {shown.map((o) => {
              const st = STATUS[o.status] || STATUS.to_prepare;
              return (
                <div key={o.id} className="border border-stone-100 rounded-xl p-3" data-testid={`shop-order-${o.orderNumber}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-stone-800">{o.orderNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                        {o.promoCode ? <span className="text-xs bg-blush-light text-deep-rose px-2 py-0.5 rounded-full font-mono">{o.promoCode}</span> : null}
                      </div>
                      <div className="text-sm text-stone-600 mt-1">{o.parentName} · <a href={`mailto:${o.email}`} className="text-deep-rose">{o.email}</a>{o.phone ? ` · ${o.phone}` : ""}</div>
                      <ul className="text-sm text-stone-600 mt-2 space-y-0.5">
                        {(o.items || []).map((i, idx) => (
                          <li key={idx}>{i.qty}× {i.name}{[i.size, i.color].filter(Boolean).length ? ` (${[i.size, i.color].filter(Boolean).join(" · ")})` : ""}</li>
                        ))}
                      </ul>
                      {o.note ? <p className="text-xs text-stone-500 italic mt-1.5">Note : {o.note}</p> : null}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-deep-rose font-medium">{fmtEur(o.total)}</div>
                      <div className="text-xs text-stone-400">{(o.paidAt || o.createdAt || "").slice(0, 10)}</div>
                    </div>
                  </div>
                  {o.paymentStatus === "paid" && o.status !== "delivered" && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {o.status !== "ready" && (
                        <button onClick={() => setOrderStatus(o, "ready")} className="inline-flex items-center gap-1.5 bg-sky-50 text-sky-700 px-3 py-2 rounded-lg text-xs" data-testid={`shop-order-ready-${o.orderNumber}`}><PackageCheck size={13} /> Prête à retirer</button>
                      )}
                      <button onClick={() => setOrderStatus(o, "delivered")} className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg text-xs" data-testid={`shop-order-delivered-${o.orderNumber}`}><Check size={13} /> Marquer remise</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopOrdersAdmin;
